import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon, ChevronDown, ChevronUp, FileText } from 'lucide-react';
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPoliciesOpen, setIsPoliciesOpen] = useState(false);
  const [isLessonsOpen, setIsLessonsOpen] = useState(false);
  const location = useLocation();
  const policyMenuRef = useRef<HTMLDivElement>(null);
  const lessonsMenuRef = useRef<HTMLDivElement>(null);
  // Updated order of navigation links
  const navLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'Tips',
    path: '/tips'
  }, {
    name: 'FAQ',
    path: '/faq'
  }, {
    name: 'Blogs',
    path: '/blogs',
    icon: <FileText size={16} className="mr-1" data-id="element-233" />
  }, {
    name: 'About Us',
    path: '/about'
  }, {
    name: 'Contact Us',
    path: '/contact'
  }];
  const lessonLinks = [{
    name: 'Standard Lessons',
    path: '/standard-lessons'
  }, {
    name: 'Intensive Lessons',
    path: '/intensive-lessons'
  }, {
    name: 'Additional Lessons',
    path: '/additional-lessons'
  }];
  const policyLinks = [{
    name: 'Terms & Conditions',
    path: '/terms'
  }, {
    name: 'Cancellation, Health & Safety Policy',
    path: '/cancellation'
  }, {
    name: 'Privacy & Cookie Policy',
    path: '/privacy'
  }];
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  const togglePoliciesMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPoliciesOpen(!isPoliciesOpen);
    // Close other dropdown when opening this one
    if (!isPoliciesOpen) {
      setIsLessonsOpen(false);
    }
  };
  const toggleLessonsMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLessonsOpen(!isLessonsOpen);
    // Close other dropdown when opening this one
    if (!isLessonsOpen) {
      setIsPoliciesOpen(false);
    }
  };
  // Close the dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (policyMenuRef.current && !policyMenuRef.current.contains(event.target as Node)) {
        setIsPoliciesOpen(false);
      }
      if (lessonsMenuRef.current && !lessonsMenuRef.current.contains(event.target as Node)) {
        setIsLessonsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Close the dropdowns when changing routes
  useEffect(() => {
    setIsPoliciesOpen(false);
    setIsLessonsOpen(false);
  }, [location.pathname]);
  return <nav className="bg-white shadow-md w-full" data-id="element-234">
      <div className="container mx-auto px-4" data-id="element-235">
        <div className="flex justify-between items-center py-4" data-id="element-236">
          {/* Logo and School Name */}
          <Link to="/" className="flex items-center" data-id="element-237">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/uQmtuFR4FdAFx95dEcdiJJ/Favicon.png" alt="Northampton Driving School Logo" className="h-14 mr-2" data-id="element-238" />
            <h1 className="text-2xl font-bold" data-id="element-239">
              <span className="text-burgundy-600" data-id="element-240">N</span>orthampton{' '}
              <span className="text-burgundy-600" data-id="element-241">D</span>riving{' '}
              <span className="text-burgundy-600" data-id="element-242">S</span>chool
            </h1>
          </Link>
          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-1" data-id="element-243">
            <Link key="/" to="/" className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/' ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} data-id="element-244">
              Home
            </Link>
            {/* Our Lessons Dropdown - Moved to appear after Home */}
            <div className="relative" ref={lessonsMenuRef} data-id="element-245">
              <button onClick={toggleLessonsMenu} className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${lessonLinks.some(link => location.pathname === link.path) ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} data-id="element-246">
                Our Lessons
                {isLessonsOpen ? <ChevronUp size={16} className="ml-1" data-id="element-247" /> : <ChevronDown size={16} className="ml-1" data-id="element-248" />}
              </button>
              {/* Dropdown Menu */}
              {isLessonsOpen && <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200 overflow-hidden" data-id="element-249">
                  <div className="py-1" data-id="element-250">
                    {lessonLinks.map(link => <Link key={link.path} to={link.path} className={`block px-4 py-2 text-sm transition-colors ${location.pathname === link.path ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} onClick={() => setIsLessonsOpen(false)} data-id="element-251">
                        {link.name}
                      </Link>)}
                  </div>
                </div>}
            </div>
            {/* Total Drive App link - Moved to appear after Our Lessons */}
            <Link to="/total-drive" className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/total-drive' ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} data-id="element-252">
              Total Drive App
            </Link>
            {/* Tips link */}
            <Link to="/tips" className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/tips' ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} data-id="element-253">
              Tips
            </Link>
            {/* FAQ link */}
            <Link to="/faq" className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/faq' ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} data-id="element-254">
              FAQ
            </Link>
            {/* Blogs link - NEW */}
            <Link to="/blogs" className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname.startsWith('/blog') ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} data-id="element-255">
              <FileText size={16} className="mr-1" data-id="element-256" />
              Blogs
            </Link>
            {/* About Us link */}
            <Link to="/about" className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/about' ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} data-id="element-257">
              About Us
            </Link>
            {/* Contact Us link */}
            <Link to="/contact" className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/contact' ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} data-id="element-258">
              Contact Us
            </Link>
            {/* Our Policies Dropdown - Moved to appear at the end */}
            <div className="relative" ref={policyMenuRef} data-id="element-259">
              <button onClick={togglePoliciesMenu} className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${policyLinks.some(link => location.pathname === link.path) ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} data-id="element-260">
                Our Policies
                {isPoliciesOpen ? <ChevronUp size={16} className="ml-1" data-id="element-261" /> : <ChevronDown size={16} className="ml-1" data-id="element-262" />}
              </button>
              {/* Dropdown Menu */}
              {isPoliciesOpen && <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200 overflow-hidden" data-id="element-263">
                  <div className="py-1" data-id="element-264">
                    {policyLinks.map(link => <Link key={link.path} to={link.path} className={`block px-4 py-2 text-sm transition-colors ${location.pathname === link.path ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} onClick={() => setIsPoliciesOpen(false)} data-id="element-265">
                        {link.name}
                      </Link>)}
                  </div>
                </div>}
            </div>
          </div>
          {/* Mobile menu button */}
          <button onClick={toggleMenu} className="md:hidden bg-burgundy-600 text-white p-2 rounded-md" aria-label="Toggle menu" data-id="element-266">
            {isOpen ? <XIcon size={24} data-id="element-267" /> : <MenuIcon size={24} data-id="element-268" />}
          </button>
        </div>
      </div>
      {/* Mobile Navigation */}
      {isOpen && <div className="md:hidden" data-id="element-269">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-gray-200" data-id="element-270">
            <Link to="/" className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/' ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} onClick={() => setIsOpen(false)} data-id="element-271">
              Home
            </Link>
            {/* Mobile Lessons Dropdown - Moved to appear after Home */}
            <div data-id="element-272">
              <button onClick={toggleLessonsMenu} className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-base font-medium ${lessonLinks.some(link => location.pathname === link.path) ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} data-id="element-273">
                <span data-id="element-274">Our Lessons</span>
                {isLessonsOpen ? <ChevronUp size={16} data-id="element-275" /> : <ChevronDown size={16} data-id="element-276" />}
              </button>
              {isLessonsOpen && <div className="pl-4 mt-1 space-y-1 border-l-2 border-gray-200 ml-3" data-id="element-277">
                  {lessonLinks.map(link => <Link key={link.path} to={link.path} className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${location.pathname === link.path ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} onClick={() => setIsOpen(false)} data-id="element-278">
                      {link.name}
                    </Link>)}
                </div>}
            </div>
            {/* Total Drive App link - Moved to appear after Our Lessons */}
            <Link to="/total-drive" className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/total-drive' ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} onClick={() => setIsOpen(false)} data-id="element-279">
              Total Drive App
            </Link>
            {/* Tips link */}
            <Link to="/tips" className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/tips' ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} onClick={() => setIsOpen(false)} data-id="element-280">
              Tips
            </Link>
            {/* FAQ link */}
            <Link to="/faq" className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/faq' ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} onClick={() => setIsOpen(false)} data-id="element-281">
              FAQ
            </Link>
            {/* Blogs link - NEW */}
            <Link to="/blogs" className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${location.pathname.startsWith('/blog') ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} onClick={() => setIsOpen(false)} data-id="element-282">
              <FileText size={16} className="mr-2" data-id="element-283" />
              Blogs
            </Link>
            {/* About Us link */}
            <Link to="/about" className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/about' ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} onClick={() => setIsOpen(false)} data-id="element-284">
              About Us
            </Link>
            {/* Contact Us link */}
            <Link to="/contact" className={`block px-3 py-2 rounded-md text-base font-medium ${location.pathname === '/contact' ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} onClick={() => setIsOpen(false)} data-id="element-285">
              Contact Us
            </Link>
            {/* Mobile Policies Dropdown - Moved to appear at the end */}
            <div data-id="element-286">
              <button onClick={togglePoliciesMenu} className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-base font-medium ${policyLinks.some(link => location.pathname === link.path) ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} data-id="element-287">
                <span data-id="element-288">Our Policies</span>
                {isPoliciesOpen ? <ChevronUp size={16} data-id="element-289" /> : <ChevronDown size={16} data-id="element-290" />}
              </button>
              {isPoliciesOpen && <div className="pl-4 mt-1 space-y-1 border-l-2 border-gray-200 ml-3" data-id="element-291">
                  {policyLinks.map(link => <Link key={link.path} to={link.path} className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${location.pathname === link.path ? 'bg-burgundy-600 text-white' : 'text-gray-700 hover:bg-burgundy-600 hover:text-white'}`} onClick={() => setIsPoliciesOpen(false)} data-id="element-292">
                      {link.name}
                    </Link>)}
                </div>}
            </div>
          </div>
        </div>}
    </nav>;
};
export default Navbar;